import React, { useState, useEffect, useContext } from 'react';
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../../../context/auth-context';
import ProductionMetricsAPI from '../../../../services/api/analytics/production-metrics-api';
import CumChart from '../components/CumChart';
import { toCalendarDate } from '../../../../utils/calendarDate';

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '0.5px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SecLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '10px',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const FormatNum = (num) => {
  if (num === null || num === undefined) return '—';
  return num.toLocaleString('en-US');
};

const actualTotal = (value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return value;
  return (
    value.totalActual ??
    value.total ??
    (Number(value.fieldActual) || 0) + (Number(value.shopActual) || 0)
  );
};

const CumulativeHoursTab = ({ projectId }) => {
  const auth = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);

  // Track viewport height so the full-screen chart can be sized to ~80% of the
  // screen. Syncfusion's chart height only accepts px / % values (not calc()/vh),
  // so we resolve it to a concrete pixel value here.
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800
  );

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const expandedChartHeight = `${Math.round(viewportHeight * 0.8)}px`;

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await ProductionMetricsAPI.getLaborCurve(
          projectId,
          auth.token
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled)
          setError(err.message || 'Failed to load labor curve data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, auth.token]);

  useEffect(() => {
    setExpandedChart(null);
  }, [projectId]);

  if (!projectId) {
    return (
      <Typography sx={{ color: 'text.secondary', mt: 4, textAlign: 'center' }}>
        Select a project to view cumulative hours data.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>
        {error}
      </Typography>
    );
  }

  if (!data || !data.weeks || data.weeks.length === 0) {
    return (
      <Typography sx={{ color: 'text.secondary', mt: 4, textAlign: 'center' }}>
        No labor curve data available.
      </Typography>
    );
  }

  // ── Transform API data into chart arrays ────────────────────────────
  const shopPlan = data.weeks.map((w) => ({
    x: toCalendarDate(w.weekEnding),
    y: w.shopPlannedCumulative,
  }));
  const shopEarned = data.weeks.map((w) => ({
    x: toCalendarDate(w.weekEnding),
    y: w.shopEarnedCumulative,
  }));
  const shopActual = data.weeks.map((w) => ({
    x: toCalendarDate(w.weekEnding),
    y: w.shopActualCumulative,
  }));

  const fieldPlan = data.weeks.map((w) => ({
    x: toCalendarDate(w.weekEnding),
    y: w.fieldPlannedCumulative,
  }));
  const fieldEarned = data.weeks.map((w) => ({
    x: toCalendarDate(w.weekEnding),
    y: w.fieldEarnedCumulative,
  }));
  const fieldActual = data.weeks.map((w) => ({
    x: toCalendarDate(w.weekEnding),
    y: w.fieldActualCumulative,
  }));
  const expandedChartTitle =
    expandedChart === 'shop'
      ? 'Shop — Cumulative Labor Hours'
      : 'Field — Cumulative Labor Hours';

  // ── Get last available PTD values ──────────────────────────────────────────
  // The actual and earned curves stop at "today", so the last non-null value is the PTD value.
  let ptdShopActual = 0;
  let ptdShopPlanned = 0;
  let ptdShopEarned = 0;
  let ptdFieldActual = 0;
  let ptdFieldPlanned = 0;
  let ptdFieldEarned = 0;
  let ptdWeekEnding = null;

  for (let i = data.weeks.length - 1; i >= 0; i--) {
    const w = data.weeks[i];
    if (w.shopActualCumulative !== null || w.fieldActualCumulative !== null) {
      ptdShopActual = w.shopActualCumulative;
      ptdShopPlanned = w.shopPlannedCumulative;
      ptdShopEarned = w.shopEarnedCumulative || 0;
      ptdFieldActual = w.fieldActualCumulative || 0;
      ptdFieldPlanned = w.fieldPlannedCumulative;
      ptdFieldEarned = w.fieldEarnedCumulative || 0;
      ptdWeekEnding = w.weekEnding;
      break;
    }
  }

  let breakdownLabel = 'Labor Breakdown — Project to Date';
  if (ptdWeekEnding) {
    const ptdDate = new Date(
      `${String(ptdWeekEnding).slice(0, 10)}T00:00:00`
    );
    if (!Number.isNaN(ptdDate.getTime())) {
      const formattedWeekEnding = ptdDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      breakdownLabel = `Labor Breakdown — As of Week Ending ${formattedWeekEnding}`;
    }
  }

  const totalPlanned = ptdShopPlanned + ptdFieldPlanned;
  const totalEarned = ptdShopEarned + ptdFieldEarned;
  const totalActual = ptdShopActual + ptdFieldActual;

  const totalBudget = data.budgetShopHrs + data.budgetFieldHrs;
  const shopPct =
    totalBudget > 0 ? (data.budgetShopHrs / totalBudget) * 100 : 0;
  const fieldPct =
    totalBudget > 0 ? (data.budgetFieldHrs / totalBudget) * 100 : 0;

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <CumChart
          title="Shop — Cumulative Labor Hours"
          budgetHrs={data.budgetShopHrs}
          plannedData={shopPlan}
          earnedData={shopEarned}
          actualData={shopActual}
          onExpand={() => setExpandedChart('shop')}
          expandAriaLabel="Expand shop cumulative hours chart"
        />
        <CumChart
          title="Field — Cumulative Labor Hours"
          budgetHrs={data.budgetFieldHrs}
          plannedData={fieldPlan}
          earnedData={fieldEarned}
          actualData={fieldActual}
          onExpand={() => setExpandedChart('field')}
          expandAriaLabel="Expand field cumulative hours chart"
        />
      </Box>

      <Dialog
        fullScreen
        open={expandedChart !== null}
        onClose={() => setExpandedChart(null)}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {expandedChartTitle}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setExpandedChart(null)}
              aria-label="Close expanded chart"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, height: 'calc(100vh - 64px)', overflow: 'auto' }}>
          {expandedChart === 'shop' && (
            <CumChart
              title="Shop — Cumulative Labor Hours"
              budgetHrs={data.budgetShopHrs}
              plannedData={shopPlan}
              earnedData={shopEarned}
              actualData={shopActual}
              height={expandedChartHeight}
            />
          )}
          {expandedChart === 'field' && (
            <CumChart
              title="Field — Cumulative Labor Hours"
              budgetHrs={data.budgetFieldHrs}
              plannedData={fieldPlan}
              earnedData={fieldEarned}
              actualData={fieldActual}
              height={expandedChartHeight}
            />
          )}
        </Box>
      </Dialog>

      <Card>
        <SecLabel>{breakdownLabel}</SecLabel>
        <Box sx={{ overflowX: 'auto', mb: 3 }}>
          <Box
            sx={{
              minWidth: 720,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              mb: 1,
              px: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontWeight: 500 }}
            >
              Scope
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textAlign: 'right',
              }}
            >
              Planned hrs
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textAlign: 'right',
              }}
            >
              Earned hrs
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textAlign: 'right',
              }}
            >
              Actual hrs
            </Typography>
          </Box>
          <Box
            sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}
          />

          <Box
            sx={{
              minWidth: 720,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="body2">
              Work Packages (Shop / off-site)
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(ptdShopPlanned)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(ptdShopEarned)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(ptdShopActual)}
            </Typography>
          </Box>
          <Box
            sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}
          />

          <Box
            sx={{
              minWidth: 720,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="body2">
              Work Packages (Field / on-site)
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(ptdFieldPlanned)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(ptdFieldEarned)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(ptdFieldActual)}
            </Typography>
          </Box>
          <Box
            sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}
          />

          <Box
            sx={{
              minWidth: 720,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              px: 2,
              py: 1,
              bgcolor: 'rgba(0,0,0,0.02)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Total Work Packages
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'right', fontWeight: 600 }}
            >
              {FormatNum(totalPlanned)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'right', fontWeight: 600 }}
            >
              {FormatNum(totalEarned)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'right', fontWeight: 600 }}
            >
              {FormatNum(totalActual)}
            </Typography>
          </Box>
          <Box
            sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}
          />

          <Box
            sx={{
              minWidth: 720,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="body2">
              General Conditions (actual-only)
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(null)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(null)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(actualTotal(data.generalConditionsActual))}
            </Typography>
          </Box>
          <Box
            sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}
          />

          <Box
            sx={{
              minWidth: 720,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="body2">Change Orders (actual-only)</Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(null)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(null)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>
              {FormatNum(actualTotal(data.changeOrdersActual))}
            </Typography>
          </Box>
          <Box
            sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}
          />

          <Box
            sx={{
              minWidth: 720,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              px: 2,
              py: 1,
              bgcolor: 'rgba(0,0,0,0.02)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Total Project Actual
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'right', fontWeight: 600 }}
            >
              {FormatNum(null)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'right', fontWeight: 600 }}
            >
              {FormatNum(null)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'right', fontWeight: 600 }}
            >
              {FormatNum(actualTotal(data.totalProjectActual) ?? totalActual)}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 1 }}>
          Budget allocation — shop vs. field
        </Typography>
        <Box
          sx={{
            display: 'flex',
            height: 8,
            width: '100%',
            borderRadius: 4,
            overflow: 'hidden',
            mb: 1,
          }}
        >
          <Box sx={{ width: `${shopPct}%`, bgcolor: '#1D9E75' }} />
          <Box sx={{ width: `${fieldPct}%`, bgcolor: '#378ADD' }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
            Shop {shopPct.toFixed(1)}%
          </Typography>
          <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
            Field {fieldPct.toFixed(1)}%
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default CumulativeHoursTab;
